import { useT } from "@transifex/react";
import { AnimatePresence, motion } from "framer-motion";
import { ElementType } from "react";
import exerciseSheetsImg from "../../assets/img/teacherTools/exerciseSheets.webp";
import testCreationImg from "../../assets/img/teacherTools/grammarTrainer.webp";
import vocabTrainerImg from "../../assets/img/teacherTools/vocabTrainer.webp";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import { cp } from "../../config";
import DesignConstants from "../../constants/DesignConstants";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { useLogger } from "../../hooks/useLogger";
import type { AnimationPhase } from "../../hooks/useOnboardingAnimation";
import { Onboarding } from "../onboarding/Onboarding";
import { type CardCommonProps, ImageSize } from "../teacherArea/card/Card.type";
import { HorizontalCard } from "../teacherArea/card/HorizontalCard";
import { VerticalCard } from "../teacherArea/card/VerticalCard";
import { Headline } from "../teacherArea/Headline";

interface AssistantIntroSectionProps {
    animationPhase: AnimationPhase;
    typewriterText: string;
    isLoggedIn: boolean;
    isTeacher: boolean;
    isAnonymousUser: boolean;
    nameParts: string;
    hasCreatedTest: boolean;
    hasCompletedProfile: boolean;
    hasVocabTraining: boolean;
}

export const AssistantIntroSection = (props: AssistantIntroSectionProps) => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();
    const { logEvent } = useLogger();
    const t = useT();

    const t_hello = t("Hello", { _tags: "Onboarding,TeacherArea" });
    const t_teacherToolsHeader = t("Assistants for your lessons", { _tags: "TeacherArea" });
    const t_saveValuableTime = t("Save valuable time with our assistants:", { _tags: "TeacherArea" });
    const t_loginAsTeacher = t("Login as a teacher to get access to our assistants:", { _tags: "TeacherArea" });
    const t_testAssistantTitle = t("Create test", { _tags: "TeacherArea" });
    const t_testAssistantSubtitle = t("Configure and customize your vocabulary tests efficiently", {
        _tags: "TeacherArea",
    });
    const t_alt_testAssistant = t(
        "An image of a test being created. A new gap-sentence exercise is added to the test.",
        { _tags: "TeacherArea,ScreenReaderOnly" }
    );
    const t_vocabTrainerAssistantTitle = t("Assign vocabulary", { _tags: "TeacherArea" });
    const t_vocabTrainerAssistantSubtitle = t("Assign targeted vocabulary to your students for training", {
        _tags: "TeacherArea",
    });
    const t_alt_vocabTrainerAssistant = t(
        "An image showing a list of selected words which will be assigned to the students to learn.",
        { _tags: "TeacherArea,ScreenReaderOnly" }
    );
    const t_alt_placeholder = t("", { _tags: "TeacherArea,ScreenReaderOnly" });
    const t_availableUponRegistration = t("Available after registration", { _tags: "TeacherArea" });
    const t_anonymousCardTitle = t("Your teacher account", { _tags: "TeacherArea" });
    const t_ctaButtonLabel = t("Register now", { _tags: "TeacherArea" });
    const t_freeAccessToAllOffers = t("<b>Free</b> access to all Phase6 offers", { _tags: "TeacherArea" });
    const t_createCustomizeVocabTests = t("Create and customize vocabulary tests", { _tags: "TeacherArea" });
    const t_assignVocabForTrainer = t("Assign vocabulary for the phase6 vocabulary trainer", {
        _tags: "TeacherArea",
    });
    const t_materialForYourLessons = t("Material and more for your lessons", { _tags: "TeacherArea" });
    const t_secureStudentDiscounts = t("Secure discounts for your students", { _tags: "TeacherArea" });

    const AnimatedPresenceFix = AnimatePresence as ElementType; // Ongoing TypeScript issue with the framer-motion package

    const isLeftCardDisabled = props.isLoggedIn && !props.isTeacher;
    const isRightCardDisabled = props.isAnonymousUser || (props.isLoggedIn && !props.isTeacher);

    const teacherTools: (CardCommonProps & { disabled: boolean })[] = [
        {
            "data-testid": "card-create-test",
            title: t_testAssistantTitle,
            subtitle: t_testAssistantSubtitle,
            href: isLeftCardDisabled
                ? ""
                : props.isLoggedIn && props.isTeacher
                ? `${cp?.cfg?.REACT_APP_PREFIX ?? cp.cfg.REACT_APP_P6_WEBSITE}/create/choose-source`
                : `${cp?.cfg?.REACT_APP_PREFIX ?? cp.cfg.REACT_APP_P6_WEBSITE}/create/anonymous`,
            image: {
                src: testCreationImg,
                alt: t_alt_testAssistant,
                size: ImageSize.BIG,
            },
            checkmarks: [],
            disabled: isLeftCardDisabled,
        },
        {
            title: t_vocabTrainerAssistantTitle,
            subtitle: t_vocabTrainerAssistantSubtitle,
            href:
                props.isLoggedIn && !isRightCardDisabled
                    ? `${cp?.cfg?.REACT_APP_PREFIX ?? cp.cfg.REACT_APP_P6_WEBSITE}/assistent`
                    : "",
            image: {
                src: vocabTrainerImg,
                alt: t_alt_vocabTrainerAssistant,
                size: ImageSize.BIG,
            },
            checkmarks: [],
            disabled: isRightCardDisabled,
        },
    ];

    return (
        <section className="flex flex-col w-full">
            {props.isLoggedIn ? (
                <>
                    <h1
                        className={`text-p6-grey-dark text-xxl font-normal transition-[margin] duration-300 ease-in-out ${
                            props.animationPhase !== "idle" && props.animationPhase !== "initial" ? "mb-m" : ""
                        }`}
                    >
                        {props.nameParts ? `${t_hello} ${props.nameParts},` : `${t_hello},`}
                    </h1>

                    <div
                        className={`transition-[min-height] duration-300 ease-in-out ${
                            props.animationPhase !== "idle" && props.animationPhase !== "initial" ? "min-h-[48px]" : ""
                        }`}
                    >
                        <AnimatedPresenceFix>
                            {props.animationPhase === "showing-onboarding" && (
                                <motion.div
                                    key="onboarding"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                >
                                    <Onboarding
                                        hasCreatedTest={props.hasCreatedTest}
                                        hasCompletedProfile={props.hasCompletedProfile}
                                        hasVocabTraining={props.hasVocabTraining}
                                    />
                                </motion.div>
                            )}

                            {(props.animationPhase === "showing-typewriter" ||
                                props.animationPhase === "show-check") && (
                                <motion.div
                                    key="typewriter"
                                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                    animate={{ opacity: 1, height: "auto", marginTop: 0 }}
                                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                >
                                    <div className="flex items-center justify-center gap-xs">
                                        <p className="text-p6-green text-xxl font-medium">{props.typewriterText}</p>
                                        {props.animationPhase === "show-check" && (
                                            <motion.div
                                                className="flex items-center justify-center bg-p6-green rounded-circle size-[2rem]"
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{
                                                    type: "spring",
                                                    stiffness: 260,
                                                    damping: 15,
                                                    duration: 0.3,
                                                    ease: "easeOut",
                                                }}
                                            >
                                                <PhaseSixIcon
                                                    name="exercise-ok"
                                                    style={{
                                                        fontSize: DesignConstants.FONT_SIZES.XXL,
                                                        color: DesignConstants.COLORS.WHITE,
                                                    }}
                                                />
                                            </motion.div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatedPresenceFix>
                    </div>
                </>
            ) : (
                <HorizontalCard
                    title={t_anonymousCardTitle}
                    colorForTransparentImage="bg-background-light"
                    image={{
                        src: exerciseSheetsImg,
                        alt: t_alt_placeholder,
                    }}
                    checkmarks={[
                        t_freeAccessToAllOffers,
                        t_createCustomizeVocabTests,
                        t_assignVocabForTrainer,
                        t_materialForYourLessons,
                        t_secureStudentDiscounts,
                    ]}
                    primaryButton={{
                        buttonId: "",
                        text: t_ctaButtonLabel,
                        role: "link",
                        href: `${cp.cfg.REACT_APP_P6_BASE_WEBSITE}lernziel.html`,
                        onClick: () => logEvent("Register Button Clicked"),
                    }}
                />
            )}

            {!props.isTeacher && (
                <section className="flex flex-col gap-s w-full mt-m">
                    <Headline
                        level={3}
                        className={`text-stone-1000 leading-none ${
                            props.animationPhase === "showing-onboarding" || !props.isLoggedIn
                                ? "text-xxl "
                                : "text-xl "
                        }`}
                        id="teacherTools"
                    >
                        {props.animationPhase === "showing-onboarding" || !props.isLoggedIn
                            ? t_teacherToolsHeader
                            : props.isTeacher
                            ? t_saveValuableTime
                            : t_loginAsTeacher}
                    </Headline>
                    <div
                        className={`flex items-stretch gap-s ${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "flex-row",
                            "flex-col"
                        )}`}
                    >
                        {teacherTools.map((teacherTool, index) => (
                            <div
                                key={`teacherTool-${index}`}
                                className="flex flex-col items-end gap-xs flex-1 w-full"
                                aria-describedby="teacherTools"
                            >
                                <VerticalCard {...teacherTool} />
                                {teacherTool.disabled && (
                                    <p className="text-stone-1000">{t_availableUponRegistration}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </section>
    );
};
